import { NextPageContext } from "next";
import { QueryClient, useQuery, UseQueryResult } from "@tanstack/react-query";
import { getPublicSmartShareProfile, getPublicUser, getUserProfile } from "../api/user";
import { getAuthorQuerie, getPublicUserQuerie, getSmartSharesLinkQuerie } from "../types/queries";
import { ShareStatus, SmartShareLinkResponse, SmartShareType, UserStatus } from "@authory-packages/core";
import { useRouter } from "next/router";
import { usePublicProfileAccessKeyContext } from "../context/publicProfileAccessKeyContext";
import { usePublicAuthorSlug } from "../hooks/usePublicAuthorSlug";
import { getSmartShareLink } from "../api/smart-share";

/**
 * This method is responsible for the logic to get author data on SSR at node level, ensures query reusability
 */
export const getProfileServer = async (queryClient: QueryClient, ctx: NextPageContext, useSmartShareEndpoint: boolean) => {
    const authorSlug = ctx.query.author as string;
    const profileAccessKey = (ctx.query.profileAccessKey as string) || "";
    const smartShareUrl = ctx.query.smartShareURL as string;
    const authorQuerie = getAuthorQuerie(authorSlug, useSmartShareEndpoint ? smartShareUrl : profileAccessKey);
    let smartShareLink: SmartShareLinkResponse | null = null;

    const author = await queryClient.fetchQuery({
        queryKey: authorQuerie,
        queryFn: () =>
            useSmartShareEndpoint
                ? getPublicSmartShareProfile(authorQuerie[1]!, authorQuerie[2]!)
                : getUserProfile(authorQuerie[1]!, authorQuerie[2]!)
    });

    if (useSmartShareEndpoint) {
        const smartShareLinkQuerie = getSmartSharesLinkQuerie(authorSlug, smartShareUrl, SmartShareType.Portfolio);
        smartShareLink = await queryClient.fetchQuery({
            queryKey: smartShareLinkQuerie,
            queryFn: () => getSmartShareLink(smartShareLinkQuerie[1], smartShareLinkQuerie[2], smartShareLinkQuerie[3])
        });
    }

    return { author, smartShareLink };
};

/**
 * Get public author client side, considering the profile status of the user
 * @param profileShareStatus ShareStatus
 */
export const getProfileClient = (profileShareStatus?: ShareStatus, useSmartShareEndpoint = false) => {
    const authorSlug = usePublicAuthorSlug();
    const router = useRouter();
    const { accessKey } = usePublicProfileAccessKeyContext();
    const isPublished = profileShareStatus === ShareStatus.Published;
    const isSecureLinkPublished = profileShareStatus === ShareStatus.SecureLink;
    const isReadyToFetch = isPublished || isSecureLinkPublished || !!accessKey?.length;
    const smartShareUrl = router.query.smartShareURL as string;

    const authorQuery = getAuthorQuerie(authorSlug, useSmartShareEndpoint ? smartShareUrl : accessKey);

    // Get the author
    const author = useQuery({
        queryKey: authorQuery,
        queryFn: () =>
            useSmartShareEndpoint ? getPublicSmartShareProfile(authorQuery[1]!, authorQuery[2]!) : getUserProfile(authorQuery[1]!, authorQuery[2]!),
        meta: { logNotFound: true },
        throwOnError: false,
        enabled: isReadyToFetch
    });

    let userSmartShareLink: UseQueryResult<SmartShareLinkResponse, Error> | null = null;

    if (useSmartShareEndpoint) {
        const smartShareLinkQuerie = getSmartSharesLinkQuerie(authorSlug, smartShareUrl, SmartShareType.Portfolio);

        userSmartShareLink = useQuery({
            queryKey: smartShareLinkQuerie,
            queryFn: () => getSmartShareLink(smartShareLinkQuerie[1], smartShareLinkQuerie[2], smartShareLinkQuerie[3]),
            throwOnError: false,
            enabled: isReadyToFetch
        });
    }

    return { author, userSmartShareLink };
};

/**
 * This method gets the public user on the server - must be called on all public pages
 * Returns nothing for an inactive account, the caller answers with `inactiveAccountResponse`
 */
export const getPublicUserServer = async (queryClient: QueryClient, ctx: NextPageContext) => {
    const authorSlug = ctx.query.author as string;

    const author = await queryClient.fetchQuery({
        queryKey: getPublicUserQuerie(authorSlug),
        queryFn: () => getPublicUser(authorSlug)
    });

    if (author.status === UserStatus.Inactive) {
        return;
    }

    return author;
};

/**
 * Get public user client use
 */
export const getPublicUserClient = () => {
    const router = useRouter();
    const authorSlug = router.query.author as string;

    // Get the author
    const author = useQuery({
        queryKey: getPublicUserQuerie(authorSlug),
        queryFn: () => getPublicUser(authorSlug),
        meta: { logNotFound: true }
    });

    return { author, userSmartShareLink: null };
};
